import React, { useState } from 'react';
import { VehicleSearchParams, SearchFilters as SearchFiltersType } from '../types/api';
import { XMarkIcon, FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface SearchFiltersProps {
  filters: VehicleSearchParams;
  onFiltersChange: (filters: VehicleSearchParams) => void;
  filterOptions?: SearchFiltersType;
  onClearFilters?: () => void;
  hasActiveFilters?: boolean;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({
  filters,
  onFiltersChange,
  filterOptions,
  onClearFilters,
  hasActiveFilters
}) => {
  const handleInputChange = (field: keyof VehicleSearchParams, value: any) => {
    try {
      onFiltersChange({
        ...filters,
        [field]: value || undefined
      });
    } catch (error) {
      console.error('Input change error:', error);
    }
  };

  const handleSourceChange = (source: string) => {
    try {
      const newFilters = { ...filters };
      
      if (source === '') {
        newFilters.source = undefined;
        newFilters.country = undefined;
      } else if (source === 'IAAI_CANADA') {
        newFilters.source = 'IAAI_CANADA';
        newFilters.country = 'CANADA';
      } else if (source === 'IAAI_USA') {
        newFilters.source = 'IAAI_USA';
        newFilters.country = 'USA';
      } else if (source === 'COPART') {
        newFilters.source = 'COPART';
        newFilters.country = 'USA';
      }
      
      onFiltersChange(newFilters);
    } catch (error) {
      console.error('Source change error:', error);
    }
  };

  return (
    <div className="space-y-5">
      {/* Keyword Search */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          🔍 Търсене
        </label>
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="VIN, марка, модел..."
            value={filters.keyword || ''}
            onChange={(e) => handleInputChange('keyword', e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
          />
        </div>
      </div>

      {/* Auction House */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          🏢 Аукционна къща
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => handleSourceChange('')}
            className={`px-3 py-2 text-sm rounded-lg border transition-all ${
              !filters.source 
                ? 'bg-primary-600 text-white border-primary-600' 
                : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
            }`}
          >
            Всички
          </button>
          <button
            type="button"
            onClick={() => handleSourceChange('IAAI_CANADA')}
            className={`px-3 py-2 text-sm rounded-lg border transition-all ${
              filters.source === 'IAAI_CANADA' 
                ? 'bg-primary-600 text-white border-primary-600' 
                : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
            }`}
          >
            🇨🇦 IAAI
          </button>
          <button
            type="button"
            onClick={() => handleSourceChange('IAAI_USA')}
            className={`px-3 py-2 text-sm rounded-lg border transition-all ${
              filters.source === 'IAAI_USA' 
                ? 'bg-primary-600 text-white border-primary-600' 
                : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
            }`}
          >
            🇺🇸 IAAI
          </button>
          <button
            type="button"
            onClick={() => handleSourceChange('COPART')}
            className={`px-3 py-2 text-sm rounded-lg border transition-all ${
              filters.source === 'COPART' 
                ? 'bg-primary-600 text-white border-primary-600' 
                : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
            }`}
          >
            🇺🇸 Copart
          </button>
        </div>
      </div>

      {/* Make */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          🚗 Марка
        </label>
        <select
          value={filters.make || ''}
          onChange={(e) => handleInputChange('make', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
        >
          <option value="">Всички марки</option>
          {filterOptions?.makes?.map((make) => (
            <option key={make} value={make}>
              {make}
            </option>
          ))}
        </select>
      </div>

      {/* Year Range */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          📅 Година
        </label>
        <div className="grid grid-cols-2 gap-3">
          <input
            type="number"
            placeholder="От"
            min={2000}
            max={2026}
            value={filters.year_from || ''}
            onChange={(e) => handleInputChange('year_from', e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <input
            type="number"
            placeholder="До"
            min={2000}
            max={2026}
            value={filters.year_to || ''}
            onChange={(e) => handleInputChange('year_to', e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          📍 Локация
        </label>
        <select
          value={filters.location || ''}
          onChange={(e) => handleInputChange('location', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
        >
          <option value="">Всички локации</option>
          {filterOptions?.locations?.map((location) => (
            <option key={location} value={location}>
              {location}
            </option>
          ))}
        </select>
      </div>

      {/* Damage Type */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          ⚠️ Тип щети
        </label>
        <select
          value={filters.damage_type || ''}
          onChange={(e) => handleInputChange('damage_type', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
        >
          <option value="">Всички типове</option>
          {filterOptions?.damage_types?.map((damage) => (
            <option key={damage} value={damage}>
              {damage}
            </option>
          ))}
        </select>
      </div>

      {/* Odometer Range */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          🔢 Пробег (км)
        </label>
        <div className="grid grid-cols-2 gap-3">
          <input
            type="number"
            placeholder="Мин"
            min="0"
            step="10000"
            value={filters.min_odometer || ''}
            onChange={(e) => handleInputChange('min_odometer', e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <input
            type="number"
            placeholder="Макс"
            min="0"
            step="10000"
            value={filters.max_odometer || ''}
            onChange={(e) => handleInputChange('max_odometer', e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Boolean Filters */}
      <div className="space-y-3 pt-2">
        <label className="flex items-center cursor-pointer group">
          <input
            type="checkbox"
            checked={filters.is_buy_now || false}
            onChange={(e) => handleInputChange('is_buy_now', e.target.checked ? true : undefined)}
            className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900">
            🛒 Само "Купи сега"
          </span>
        </label>

        <label className="flex items-center cursor-pointer group">
          <input
            type="checkbox"
            checked={filters.is_featured || false}
            onChange={(e) => handleInputChange('is_featured', e.target.checked ? true : undefined)}
            className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900">
            ⭐ Само препоръчани
          </span>
        </label>
      </div>

      {/* Clear Filters Button */}
      {hasActiveFilters && onClearFilters && (
        <button
          type="button"
          onClick={onClearFilters}
          className="w-full mt-4 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
        >
          Изчисти филтрите
        </button>
      )}
    </div>
  );
};

// Mobile Filter Drawer Component
interface MobileFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
}

export const MobileFilterDrawer: React.FC<MobileFilterDrawerProps> = ({
  isOpen,
  onClose,
  children,
  hasActiveFilters,
  onClearFilters
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed inset-y-0 left-0 w-full max-w-sm bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center">
            <FunnelIcon className="w-6 h-6 text-primary-600 mr-2" />
            <h2 className="text-lg font-bold text-gray-900">Филтри</h2>
            {hasActiveFilters && (
              <span className="ml-2 bg-primary-600 text-white text-xs px-2 py-1 rounded-full">
                Активни
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {children}
        </div>
        
        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-2">
          {hasActiveFilters && onClearFilters && (
            <button
              onClick={onClearFilters}
              className="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Изчисти филтрите
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            Приложи филтрите
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchFilters;
